/**
 * @module Model
 */

import { OrderTemplate } from '@feature/orders/shared';
import { CustomFieldValue } from '@app/components/custom-fields/shared/custom-field.model';
import { BankDetailsStatus } from '@feature/profiles/shared';

/**
 * Class representing a user profile in the application.
 *
 * This class encapsulates various properties related to a profile of an individual or a company.
 * It is used to manage and represent profiles within the application.
 *
 * @class
 * @model
 */
export class Profile {
	/**
	 * The unique identifier for the profile.
	 * @type {number}
	 */
	id: number;

	/**
	 * The current signup step for the user.
	 * @type {string | undefined}
	 */
	signupStep?: string;

	/**
	 * The website associated with the profile.
	 * @type {string | undefined}
	 */
	website?: string;

	/**
	 * The phone number associated with the profile.
	 * @type {string | undefined}
	 */
	phone?: string;

	/**
	 * The address associated with the profile.
	 * @type {Address | undefined}
	 */
	address?: Address;

	/**
	 * The percentage fee associated with the user's union membership.
	 * @type {number}
	 */
	unionPercent: number;

	/**
	 * The percentage fee associated with the Eljin paymaster operations.
	 * @type {number}
	 */
	paymasterPercent: number;

	/**
	 * Indicates whether reverse calculation is enabled.
	 * @type {boolean}
	 */
	reverseCalculation: boolean;

	/**
	 * The status of the bank details associated with the user.
	 * @type {BankDetailsStatus}
	 */
	bankDetailsStatus: BankDetailsStatus;

	/**
	 * Array of custom field values associated with the profile (optional).
	 * @type {Array<CustomFieldValue> | undefined}
	 */
	customFieldValues?: Array<CustomFieldValue>;

	/**
	 * Array of order templates (union or non-performance) used in order calculations (optional).
	 * @type {Array<OrderTemplate> | undefined}
	 */
	orderTemplates?: Array<OrderTemplate>;

	/**
	 * Default order template used in order calculations (optional).
	 * @type {OrderTemplate | undefined}
	 */
	defaultTemplate?: OrderTemplate;

	/**
	 * Gender associated with the profile (optional).
	 * @type {Gender | undefined}
	 */
	gender?: Gender;

	/**
	 * Title associated with the profile (optional).
	 * @type {Title | undefined}
	 */
	title?: Title;

	/**
	 * The first name of the user or company owner (optional).
	 * @type {string}
	 */
	firstName?: string;

	/**
	 * The middle name of the user or company owner (optional).
	 * @type {string}
	 */
	middleName?: string;

	/**
	 * The last name of the user or company owner (optional).
	 * @type {string}
	 */
	lastName?: string;

	/**
	 * The full name of the company associated with the user (optional).
	 * @type {string}
	 */
	companyFullName?: string;

	/**
	 * Getter that formats the display name for the user.
	 *
	 * This getter returns the company full name if available; otherwise, it returns the user's
	 * full name, optionally including the middle name in parentheses.
	 *
	 * @getter
	 * @returns {string} The formatted display name for the user.
	 */
	get text(): string {
		if (this.companyFullName) return this.companyFullName;
		return this.middleName
			? `${this.firstName} (${this.middleName}) ${this.lastName}`
			: `${this.firstName} ${this.lastName}`;
	}
}

/**
 * Class representing a user signatory in the application.
 *
 * This class encapsulates the properties related to a contract that describes
 * the amount to be withdrawn to the H&R account.
 *
 * @class
 * @model
 */
export class UserSignatory {
	/**
	 * The unique identifier for the user's union signatory.
	 * @type {number}
	 */
	id: number;

	/**
	 * The unique identifier for the user's signatory contract.
	 * @type {string}
	 */
	usSignatoryId: string;

	/**
	 * The name of the user signatory.
	 * @type {string}
	 */
	usSignatoryName: string;

	/**
	 * The name of the union associated with the user's union signatory.
	 * @type {string}
	 */
	usSignatoryUnionName: string;

	/**
	 * The percentage associated with the user's union.
	 * @type {number}
	 */
	usUnionPercentage: number;
}

/**
 * Class representing an address in the application.
 *
 * It is used to manage and represent address data associated
 * with an individual or a company entities.
 *
 * @class
 * @model
 */
export class Address {
	/**
	 * The street address.
	 * @type {string}
	 */
	street: string;

	/**
	 * The apartment or unit number.
	 * @note Optional in user interface.
	 * @type {string}
	 */
	streetApt: string;

	/**
	 * The city of the address.
	 * @type {string}
	 */
	city: string;

	/**
	 * The postal code for the address.
	 * @type {string}
	 */
	postCode: string;

	/**
	 * The unique identifier for the country.
	 * @type {number}
	 */
	countryId: number;

	/**
	 * The name of the country.
	 * @type {string}
	 */
	countryName: string;

	/**
	 * The code of the country.
	 * @type {string}
	 */
	countryCode?: string;

	/**
	 * The unique identifier for the state.
	 * @type {number}
	 */
	stateId: number;

	/**
	 * The region associated with the address.
	 * @note ETT-377 Allow payer to switch country/state.
	 * @type {string}
	 */
	region: string;

	/**
	 * The name of the state.
	 * @type {string}
	 */
	stateName: string;
}

/**
 * Class representing an email CC recipients associated with the user.
 *
 * This class encapsulates the properties related to an email CC recipient.
 * It is used to manage and represent CC recipients in email communications
 * within the application.
 *
 * @class
 * @model
 */
export class EmailCC {
	/**
	 * The email address of the CC recipient.
	 * @type {string}
	 */
	email: string;

	/**
	 * Creates an instance of the EmailCC class.
	 *
	 * @constructor
	 * @param {string} email - The email address of the CC recipient.
	 */
	constructor(email: string) {
		this.email = email;
	}
}

/**
 * Class representing a gender in the application.
 *
 * This class encapsulates properties related to gender.
 *
 * @class
 * @model
 */
export class Gender {
	/**
	 * The unique identifier for the gender.
	 * @type {number}
	 */
	id: number;

	/**
	 * The title of the gender (e.g., 'Male', 'Female').
	 * @type {string}
	 */
	title: string;

	/**
	 * An alias or shorthand representation of the gender.
	 * @type {string}
	 */
	alias: string;
}

/**
 * Class representing an honorific or a title in a short statement.
 *
 * In English, the most used are Mr, Mrs, Miss and Ms.
 * There are also professional honorifics such as Dr and Rev.
 *
 * @class
 * @model
 *
 * @see [Title vs honorific](https://english.stackexchange.com/questions/317645/title-vs-honorific)
 */
export class Title {
	/**
	 * The unique identifier for the title.
	 * @type {number}
	 */
	id: number;

	/**
	 * The name of the title (e.g., 'Mr.', 'Ms.', 'Dr.').
	 * @type {string}
	 */
	name: string;

	/**
	 * An alias or shorthand representation of the title.
	 * @type {string}
	 */
	alias: string;
}

/**
 * Represents a user role option interface.
 *
 * @interface UserRoleOption
 */
export interface UserRoleOption {
	/**
	 * The alias for the user role.
	 * @type {string}
	 */
	alias: string;

	/**
	 * The role for the user role.
	 * @type {string}
	 */
	role: string;

	/**
	 * The icon for the user role (optional).
	 * @type {string}
	 */
	icon?: string;

	/**
	 * The title for the user role.
	 * @type {string}
	 */
	title: string;

	/**
	 * The subtitle for the user role (optional).
	 * @type {string}
	 */
	subTitle?: string;

	/**
	 * The list of option features for the user role (optional).
	 * @type {Array<UserRoleOptionFeature>}
	 */
	features?: Array<UserRoleOptionFeature>;

	/**
	 * The button label for the user role.
	 * @type {string}
	 */
	buttonLabel: string;
}

/**
 * Represents an option feature interface associated with a user role option.
 *
 * @interface UserRoleOptionFeature
 */
export interface UserRoleOptionFeature {
	/**
	 * The text to display for the feature option.
	 * @type {string}
	 */
	text: string;

	/**
	 * The icon to display for the feature option (optional).
	 * @type {string}
	 */
	icon?: string;

	/**
	 * Whether the feature option is negative (optional).
	 * @type {boolean}
	 */
	isNegative?: boolean;
}
